import React, { useCallback } from 'react';
import { connect } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import { Box, Stack, Typography, useTheme } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';

import DataTable from '../common/DataTable';
import EntityCell from '../common/EntityCell';
import StatusChip from '../common/StatusChip';
import RowActions from '../common/RowActions';
import { frequencyLabel, scheduleToFrequency, PAUSE_REASON_LABEL } from './connectorMeta';
import { listJobs, triggerJob, updateJob, deleteJob } from '../../redux/actions/ingestion/jobs';

const rel = (d) => { try { return d ? formatDistanceToNow(new Date(d), { addSuffix: true }) : '—'; } catch (_e) { return '—'; } };
const STATUS_TONE = { active: 'success', paused: 'warning', completed: 'success', failed: 'error', archived: 'neutral' };

const ConnectorJobs = ({ jobs, isLoading, sourceId, listJobs, triggerJob, updateJob, deleteJob }) => {
  const theme = useTheme();
  const navigate = useNavigate();

  const refresh = useCallback(() => { if (sourceId) listJobs({ sourceId }); }, [listJobs, sourceId]);

  const onRun = (j) => { triggerJob(j.job_id); setTimeout(refresh, 800); };
  const onPause = (j) => { updateJob(j.job_id, { status: 'paused' }); setTimeout(refresh, 500); };
  const onResume = (j) => { updateJob(j.job_id, { status: 'active' }); setTimeout(refresh, 500); };
  const onDelete = (j) => { if (window.confirm(`Delete sync "${j.name}"?`)) { deleteJob(j.job_id); setTimeout(refresh, 500); } };

  const columns = [
    {
      key: 'identity', label: 'Job', width: 130,
      render: (j) => <EntityCell id={j.job_id} tone={STATUS_TONE[j.status] || 'neutral'} label={j.status} />,
    },
    {
      key: 'name', label: 'Sync',
      render: (j) => (
        <Box sx={{ minWidth: 0 }}>
          <Typography sx={{ fontSize: 13, fontWeight: 600 }} noWrap>{j.name || j.job_id}</Typography>
          {j.config?.table && <Typography sx={{ fontSize: 11, color: 'text.secondary' }} noWrap>table: {j.config.table}</Typography>}
          {j.status === 'paused' && j.pause_reason && (
            <Typography sx={{ fontSize: 11, color: theme.palette.warning.main }} noWrap title={j.pause_reason}>
              {PAUSE_REASON_LABEL(j.pause_kind)}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'schedule', label: 'Frequency', width: 130,
      render: (j) => (
        <Stack direction="row" spacing={0.5} alignItems="center">
          <ScheduleIcon sx={{ fontSize: 14, color: 'text.secondary' }} />
          <Typography sx={{ fontSize: 13 }}>{frequencyLabel(scheduleToFrequency(j.schedule))}</Typography>
        </Stack>
      ),
    },
    {
      key: 'incremental', label: 'Mode', width: 110,
      render: (j) => (
        <StatusChip tone={j.config?.incremental?.watermark_column ? 'info' : 'neutral'}
          label={j.config?.incremental?.watermark_column ? 'Incremental' : 'Full refresh'} variant="soft" />
      ),
    },
    { key: 'last_run', label: 'Last run', width: 130, render: (j) => <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>{rel(j.last_run_at)}</Typography> },
    {
      key: 'runs', label: 'Runs', width: 90, align: 'right',
      render: (j) => (
        <Typography sx={{ fontSize: 12, color: 'text.secondary' }}>
          <span style={{ color: theme.palette.success.main }}>{j.success_count || 0}</span>
          {' / '}
          <span style={{ color: (j.failure_count ? theme.palette.error.main : undefined) }}>{j.failure_count || 0}</span>
        </Typography>
      ),
    },
    {
      key: 'actions', label: '', width: 80, align: 'right', stopRowClick: true,
      render: (j) => (
        <RowActions
          onOpen={() => navigate(`/datasources/${sourceId}/jobs/${j.job_id}/edit`)}
          openLabel="Edit sync"
          items={[
            { label: 'Run now', icon: PlayArrowIcon, onClick: () => onRun(j) },
            j.status === 'paused'
              ? { label: 'Resume', icon: PlayArrowIcon, onClick: () => onResume(j) }
              : { label: 'Pause', icon: PauseIcon, onClick: () => onPause(j) },
            { label: 'Edit', icon: EditIcon, onClick: () => navigate(`/datasources/${sourceId}/jobs/${j.job_id}/edit`) },
            { divider: true },
            { label: 'Delete', icon: DeleteOutlineIcon, danger: true, onClick: () => onDelete(j) },
          ]}
        />
      ),
    },
  ];

  return (
    <DataTable
      columns={columns}
      rows={jobs}
      loading={isLoading}
      getRowKey={(j) => j.job_id}
      onRowClick={(j) => navigate(`/datasources/${sourceId}/jobs/${j.job_id}/edit`)}
      emptyState={{
        icon: <ScheduleIcon />, title: 'No sync jobs yet',
        message: 'Add a sync to start pulling data from this source on a schedule.',
      }}
    />
  );
};

export default connect(null, { listJobs, triggerJob, updateJob, deleteJob })(ConnectorJobs);
